<template>
  <v-card class="pb-8">
    <v-data-table
      v-model="selected"
      :headers="headersFiltered"
      :items="filteredAgents"
      :loading="loading"
      :loading-text="loadingText"
      item-key="id"
      :footer-props="{
        itemsPerPageText: 'Eventos por página',
        itemsPerPageOptions: itemsPerPageOptions,
        pageText: `${page} de ${totalPages}`
      }"
      fixed-header
      calculate-widths
      :height="height"
      id="agents__dataTable"
      :items-per-page.sync="itemsPerPage"
      :page.sync="page"
      :sort-by.sync="sortedBy"
      :sort-desc.sync="sortedDesc"
      @update:items-per-page="highlightSearchMatchAndFetchNotifications()"
      @update:page="highlightSearchMatchAndFetchNotifications()"
      @update:sort-by="highlightSearchMatchAndFetchNotifications()"
      @update:sort-desc="highlightSearchMatchAndFetchNotifications()"
      :no-data-text="dataMessage"
      must-sort
      ref="AgentsList"
    >
      <template v-slot:top>
        <div class="card--title">
          <v-toolbar height="57" rounded="lg" flat>
            <v-toolbar-title
              >{{
                $t(
                  'fleet_management.agents.data_table.labels.data_table_toolbar_title'
                )
              }}
              ({{ totalAgents }})</v-toolbar-title
            >
            <v-spacer></v-spacer>
            <router-link id="idEventosAgregar" :to="{ name: 'agent-create' }">
              <v-btn
                v-if="isAdmin || isAdminSubFleet"
                text
                @click="addAgentsInp"
              >
                <SvgIcon class="tertiary--text" iconType="icon_agents_add" />
                <span class="text-capitalize text-body-2">{{
                  $t(
                    'fleet_management.agents.data_table.labels.data_table_toolbar_button'
                  )
                }}</span>
              </v-btn>
            </router-link>
          </v-toolbar>
        </div>
      </template>
      <template v-slot:item.label="{ item }">
        <v-tooltip top content-class="secondary white--text text-subtitle-2">
          <template v-slot:activator="{ on, attrs }">
            <span
              class="secondary--text searchable label-links"
              style="cursor: pointer;"
              v-bind="attrs"
              v-on="on"
              @click="showAgentDetail(item)"
            >
              {{ item.label }}
            </span>
          </template>
          <span class="agent__label searchable">{{ item.label }}</span>
          <div class="tooltip-arrow"></div>
        </v-tooltip>
      </template>
      <template v-slot:item.conditions="{ item }">
        <div v-if="item.conditions">
          <div
            v-for="(condition, index) in Object.keys(item.conditions)"
            :key="index"
            class="text-left"
          >
            <div v-if="condition === 'mrn:agent_condition:movement_start'">
              <div v-if="item.conditions[condition]">
                <span class="mr-1"
                  ><strong class="searchable">
                    {{ 
                      $t(
                          'fleet_management.agents.data_table.labels.condition_start_movement'
                        )  
                    }}
                  </strong>
                  <span>
                    {{ 
                      $t(
                        'fleet_management.agents.data_table.labels.is_activated_in', { time: item.conditions[condition].minimum_in_movement_time / 60 }
                        ) 
                    }}
                  </span>
                  <span>
                    {{ 
                      $t(
                        'fleet_management.agents.data_table.labels.maximum_stop', { time: item.conditions[condition].minimum_stop_time / 60 }
                        ) 
                    }}  
                  </span
                  >
                </span>
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:ignition'"
              class="secondary--text"
            >
              <div>
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      `fleet_management.agents.data_table.labels.condition_ignition_on_${item.conditions[condition].contact_on}`
                    )
                  }}</strong></span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:main_power'"
              class="secondary--text"
            >
              <div>
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      `fleet_management.agents.data_table.labels.condition_main_power_on_${item.conditions[condition].main_power_on}`
                    )
                  }}</strong></span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:no_report'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_no_report'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:detention'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_detention'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:fuel_cut_off'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    `fleet_management.agents.data_table.labels.condition_fuel_cut_off_${item.conditions[condition].fuel_cut_off}`
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:speed_limit'"
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_speed_limit'
                    )
                  }}</strong></span
                >
                <span class="searchable"
                  >{{
                    item.conditions['mrn:agent_condition:speed_limit']
                      .speed_limit
                  }}
                  Km/h</span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:low_battery'"
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_low_battery'
                    )
                  }}</strong></span
                >
                <span class="searchable"
                  >{{
                    item.conditions['mrn:agent_condition:low_battery']
                      .low_level
                  }}&#37;</span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:ralenti'"
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_ralenti'
                    )
                  }}</strong></span
                >
                <span class="searchable">{{
                  item.conditions['mrn:agent_condition:ralenti'].rpm_limit
                }}</span>
              </div>
            </div>
            <div
              v-if="
                condition === 'mrn:agent_condition:fuel_consumption_deviation'
              "
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_fuel_consumption_deviation'
                    )
                  }}</strong></span
                >
                <span class="searchable">
                  {{
                    getDeviationPercentage(
                      item.conditions[
                        'mrn:agent_condition:fuel_consumption_deviation'
                      ].fuel_deviation
                    )
                  }}&#37;</span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:fuel_charge'"
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_fuel_charge'
                    )
                  }}</strong></span
                >
                <span class="searchable">
                  {{
                    getFuelTankPercentage(
                      item.conditions['mrn:agent_condition:fuel_charge']
                        .fuel_limit
                    )
                  }}&#37;</span
                >
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:temperature'"
              class="secondary--text"
            >
              <div v-if="item.conditions[condition]" class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable"
                    >{{
                      $t(
                        'fleet_management.agents.data_table.labels.condition_temperature_sensor'
                      )
                    }}:</strong
                  ></span
                >
                <span class="searchable">
                  Temperatura Mínima:
                  {{ formatTemperature(item.conditions[condition].min_temp) }}°
                </span>
                -
                <span class="searchable">
                  Temperatura Máxima:
                  {{ formatTemperature(item.conditions[condition].max_temp) }}°
                </span>
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:multi_temperature'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable"
                  >{{
                      $t(
                        'fleet_management.agents.data_table.labels.condition_temperature_multi_sensor'
                      )
                    }}:</strong
                ></span
              >
              <div
                v-for="(value, key, index) in item.conditions['mrn:agent_condition:multi_temperature']"
                :key="key"
                v-if="key !== 'service_name'"
                class="d-inline-block mr-2"
              >
                <strong class="searchable">T{{ key.replace('temperature', '') }}:</strong>
                <span class="searchable">
                  Min: {{ formatTemperature(value.min_temp) }}° -
                  Max: {{ formatTemperature(value.max_temp) }}°
                </span>
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:geofence'"
              class="secondary--text"
            >
              <span class="mr-1">
                <strong
                  v-if="
                    item.conditions['mrn:agent_condition:geofence'].type == 'in'
                  "
                  >{{
                    $t(
                      'fleet_management.agents.data_table.labels.condition_geofence_type_in'
                    )
                  }}</strong
                >
                <strong v-else>{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_geofence_type_out'
                  )
                }}</strong>
              </span>
              <v-tooltip
                top
                content-class="secondary white--text text-subtitle-2"
              >
                <template v-slot:activator="{ on, attrs }">
                  <span v-if="index !== 0">, </span>
                  <span
                    class="secondary--text searchable label-links"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{
                      getLimitedGeofencesText(
                        item.conditions['mrn:agent_condition:geofence']
                      )
                    }}...
                  </span>
                </template>
                <template>
                  <span class="label-links searchable"
                    >
                    {{ 
                      $t(
                          'fleet_management.agents.data_table.labels.see_more_zones_in_detail'
                        )  
                    }}
                    </span
                  >
                </template>
                <div class="tooltip-arrow"></div>
              </v-tooltip>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:panic_button'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_panic_button'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:lasso_disconnected'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_lasso_disconnected'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:driver_door'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_driver_door'
                  )
                }}</strong></span
              >
            </div>

            <div
              v-if="condition === 'mrn:agent_condition:driver_door_normal'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_driver_door_normal'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:cargo_gate'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_cargo_gate'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:cargo_gate_normal'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_cargo_gate_normal'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:semi_detach'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_semi_detach'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:semi_detach_normal'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_semi_detach_normal'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:tamper_sensor'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_tamper_sensor'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="
                condition === 'mrn:agent_condition:generic_unauthorized_stop'
              "
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_unauthorized_stop'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="
                condition === 'mrn:agent_condition:generic_put_into_service'
              "
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_put_into_service'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="
                condition === 'mrn:agent_condition:generic_taken_out_of_service'
              "
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_taken_out_of_service'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_still_detached'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_still_detached'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_event_test'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_event_test'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_seat_passenger'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_seat_passenger'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_delivery'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_delivery'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_sharped_turn'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_sharped_turn'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:generic_hard_brake'"
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_hard_brake'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="
                condition === 'mrn:agent_condition:generic_hard_acceleration'
              "
              class="secondary--text"
            >
              <span class="mr-1"
                ><strong class="searchable">{{
                  $t(
                    'fleet_management.agents.data_table.labels.condition_generic_hard_acceleration'
                  )
                }}</strong></span
              >
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:engine_temperature'"
              class="secondary--text"
            >
              <div v-if="item.conditions[condition]" class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable"
                    >{{
                      $t(
                        'fleet_management.agents.data_table.labels.condition_engine_temperature'
                      )
                    }}:</strong
                  ></span
                >
                <span class="searchable">
                  {{ 
                    $t(
                      'fleet_management.agents.data_table.labels.condition_geofence_type_out'
                    )  
                  }}
                  {{ formatTemperature(item.conditions[condition].max_temp) }}°
                </span>
              </div>
            </div>
            <div
              v-if="condition === 'mrn:agent_condition:excessive_rpm'"
              class="secondary--text"
            >
              <div class="d-inline mr-1">
                <span class="mr-1"
                  ><strong class="searchable">{{
                    $t(
                      'fleet_management.agents.data_table.labels.temperature_max'
                    )
                  }}</strong></span
                >
                <span class="searchable">{{
                  item.conditions['mrn:agent_condition:excessive_rpm'].rpm_limit
                }} RPM</span>
              </div>
            </div>
          </div>
          <div class="text-left">
            <div
              class="d-inline"
              v-if="
                item.window >= 0 &&
                  hasNoTolerance(item.conditions) &&
                  isNotLowBattery(item)
              "
            >
              <span class="mr-1"
                ><strong class="secondary--text">{{
                  $t(
                    `fleet_management.agents.time_limit.labels.${tolerancia(
                      item.conditions
                    )}`
                  )
                }}</strong></span
              >
              <span class="searchable secondary--text">{{
                item.window | parseWindow
              }}</span>
            </div>
          </div>
        </div>
      </template>

      <template v-slot:item.scheduling="{ item }">
        <div v-if="item.scheduling">
          <v-icon>
            mdi-calendar-clock
          </v-icon>
        </div>
      </template>

      <template
        v-slot:item.user_notifications="{ item }"
        v-if="isAdmin || isAdminSubFleet"
      >
        <v-menu
          top
          rounded-xl
          :open-on-hover="
            filterNotificationsByAgentId(item.id).length ||
              filterNotificationsGroupByAgentId(item.id).length
          "
          :open-on-click="false"
          transition="scale-transition"
          min-width="30px"
          allow-overflow
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-badge
                :content="
                  filterNotificationsByAgentId(item.id).length +
                    filterNotificationsGroupByAgentId(item.id).length
                "
                :value="
                  filterNotificationsByAgentId(item.id).length +
                    filterNotificationsGroupByAgentId(item.id).length
                "
                color="tertiary"
                overlap
              >
                <SvgIcon
                  iconType="icon_user"
                  :class="[
                    filterNotificationsByAgentId(item.id).length === 0 &&
                    filterNotificationsGroupByAgentId(item.id).length === 0
                      ? 'grey--text'
                      : ''
                  ]"
                />
              </v-badge>
            </div>
          </template>
          <v-card max-width="250" class="pl-2 scrollable-card">
            <v-chip-group
              v-if="filterNotificationsByAgentId(item.id).length"
              column
            >
              <v-chip
                v-for="user in filterNotificationsByAgentId(item.id)"
                :key="user.agentId"
                small
                ><v-icon left small>
                  mdi-account
                </v-icon>
                {{ user.fullName }}
              </v-chip>
            </v-chip-group>
            <v-chip-group
              v-if="filterNotificationsGroupByAgentId(item.id).length"
              column
            >
              <v-chip
                v-for="group in filterNotificationsGroupByAgentId(item.id)"
                :key="group.agentId"
                small
                label
                ><v-icon :color="group.color" left small>
                  mdi-account-group
                </v-icon>
                <span :style="{ color: group.color }">{{
                  group.groupName
                }}</span>
              </v-chip>
            </v-chip-group>
          </v-card>
        </v-menu>
      </template>

      <template v-slot:item.things="{ item }">
        <v-menu
          top
          rounded-xl
          :open-on-hover="item.things"
          :open-on-click="false"
          transition="scale-transition"
          min-width="30px"
          allow-overflow
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-badge
                :content="contentBadge(item)"
                :value="contentBadge(item)"
                color="tertiary"
                overlap
              >
                <SvgIcon
                  iconType="icon_truck"
                  :class="[contentBadge(item) == 0 ? 'grey--text' : '']"
                />
              </v-badge>
            </div>
          </template>
          <v-card max-width="250" class="pl-2 scrollable-card">
            <v-chip-group v-if="item.things" column>
              <v-chip
                v-for="thing in Object.values(item.things)"
                :key="thing"
                small
              >
                {{ thing }}
              </v-chip>
            </v-chip-group>
          </v-card>
        </v-menu>
      </template>

      <template v-slot:item.actions="{ item, index }">
        <v-menu
          offset-x
          nudge-left="12"
          nudge-top="4"
          left
          rounded-xl
          content-class="white pa-1 v-menu__action-column"
        >
          <template v-slot:activator="{ on, attrs, value }">
            <v-btn
              :id="`idEventosKebabMenu${index}`"
              :class="[value ? '' : 'btn__activator-not-focus']"
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <div>
            <v-btn
              :id="`idEventosEditarAgente${index}`"
              text
              @click="showAgentDetail(item)"
            >
              <v-icon small color="secondary" class="mr-1">
                mdi-information-outline
              </v-icon>
              <span class="text-body-2 text-capitalize">{{
                $t('fleet_management.agents.data_table.labels.button_detail')
              }}</span>
            </v-btn>
            <v-btn
              v-if="isAdmin || item.created_by === currentUser"
              :id="`idEventosEliminarAgente${index}`"
              @click="deleteAgent(item)"
              text
            >
              <v-icon small color="primary" class="mr-1">
                mdi-trash-can-outline
              </v-icon>
              <span class="text-body-2 text-capitalize">{{
                $t('fleet_management.agents.data_table.labels.button_delete')
              }}</span>
            </v-btn>
          </div>
        </v-menu>
      </template>
    </v-data-table>
    <Dialog
      idSubmitButton="idEventosSiEliminar"
      idCancelButton="idEventosNoEliminar"
      :showDialog="showDialog"
      :text="dialog.text"
      :closeButtonText="dialog.closeButtonText"
      :submitButtonText="dialog.acceptButtonText"
      :oneButton="dialog.oneButtonDialog"
      :oneButtonText="dialog.oneButtonText"
      :type="dialog.type"
      :loading="dialog.loading"
      @cancelButtonPressed="cancelDialog"
      @submitButtonPressed="submitAgentDelete"
      @oneButtonAction="cancelDialog"
      class="ma-0"
    />
  </v-card>
</template>

<script>
import { i18n } from '@leapsight/portal'
import { SvgIcon, firebaseServices } from '@leapsight/styleguide'
import { FirebaseTagNames } from '@leapsight/portal'
import { mapActions, mapGetters } from 'vuex'
import Dialog from '@/components/dialog/Dialog'
import store from '@/store'
import Mark from 'mark.js'
import agentsHelpers from '@/helpers/agents/agentsHelpers'
import { GlobalHelpers } from '@/helpers/mainExports.js'

export default {
  name: 'AgentsList',
  components: {
    SvgIcon,
    Dialog
  },
  filters: {
    parseWindow: function(window) {
      if (window > 0) {
        const days = Math.floor(window / (3600 * 24))
        const hours = Math.floor((window % (3600 * 24)) / 3600)
        const minutes = Math.floor((window % 3600) / 60)
        const seconds = Math.floor(window % 60)
        const dDisplay = days > 0 ? `${days} d ` : ''
        const hDisplay = hours > 0 ? `${hours} hs ` : ''
        const mDisplay = minutes > 0 ? `${minutes} min ` : ''
        const sDisplay = seconds > 0 ? `${seconds} seg ` : ''
        return dDisplay + hDisplay + mDisplay + sDisplay
      } else return 0
    }
  },
  data() {
    return {
      itemsPerPage: 10,
      itemsPerPageOptions: [5, 10, 15],
      page: 1,
      agentsWithFetchedNotifications: [],
      sortedBy: 'label',
      currentUser: localStorage.getItem('user_id'),
      sortedDesc: false,
      eventsCalled: 0,
      hover: false,
      selected: [],
      dataMessage: `${i18n.t(
        'fleet_management.agents.data_table.labels.data_table_no_data'
      )}`,
      searchMatch: [],
      matchPosition: 1,
      inputTerm: '',
      markInstance: {},
      loadingText: i18n.t(
        'fleet_management.agents.data_table.labels.loading_text'
      ),
      context: '',
      loading: false,
      dataTableHeight: 0,
      headers: [
        {
          text: i18n.t(
            'fleet_management.agents.data_table.labels.data_table_header_name'
          ),
          value: 'label',
          align: 'center',
          class: 'secondary--text'
        },
        {
          text: i18n.t(
            'fleet_management.agents.data_table.labels.data_table_header_conditions'
          ),
          value: 'conditions',
          align: 'start',
          width: '40%',
          class: 'secondary--text',
          sort: (a, b) => {
            const x = Object.keys(a)[0],
              y = Object.keys(b)[0]
            return x == y ? 0 : x > y ? 1 : -1
          }
        },
        {
          text: i18n.t(
            'fleet_management.agents.data_table.labels.data_table_header_time_restriction'
          ),
          width: 170,
          value: 'scheduling',
          align: 'center',
          class: 'secondary--text',
          sortable: false
        },
        {
          text: i18n.t(
            'fleet_management.agents.data_table.labels.data_table_header_entities'
          ),
          width: 150,
          value: 'things',
          align: 'center',
          class: 'secondary--text',
          sortable: false
        },
        {
          text: '',
          value: 'actions',
          align: 'start',
          sortable: false
        }
      ],
      // agents: [],
      agentToDelete: '',
      showDialog: false,
      dialogDefault: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButtonText: '',
        oneButtonDialog: false
      },
      dialog: {
        text: '',
        closeButtonText: i18n.t('general.dialogComponent.closeButtonText'),
        acceptButtonText: i18n.t('general.dialogComponent.acceptButtonText'),
        type: '',
        loading: false,
        oneButtonText: '',
        oneButtonDialog: false
      },
      isActive: false,
      resetSearchInput: false
    }
  },
  methods: {
    ...mapActions(['setSearchMatch', 'setSearch']),
    ...mapActions('events', ['fillAgents']),
    ...mapActions('notifications', ['fillNotification']),
    addAgentsInp() {
      firebaseServices.logEvent(
        firebaseServices.analytics,
        FirebaseTagNames.BTN_FLEET_MANAGEMENT_AGENTS_TABLE_ADD
      )
    },
    getDeviationPercentage(percentage) {
      return (percentage * 100).toFixed(0)
    },
    getLimitedGeofences(geofenceCondition) {
      const geofencesLimit = Object.values(geofenceCondition.geofences)
        .map(item => item.trim())
        .filter(item => item !== '')
        .slice(0, 6)
      return geofencesLimit
    },
    getLimitedGeofencesText(geofenceCondition) {
      const limitedGeofences = this.getLimitedGeofences(geofenceCondition)
      return limitedGeofences.join(', ')
    },
    getFuelTankPercentage(percentage) {
      return (percentage * 100).toFixed(0)
    },
    formatTemperature(value) {
      const intValue = Math.floor(value)
      const floatValue = value - intValue

      if (floatValue > 0) {
        return value.toFixed(1)
      } else {
        return intValue.toFixed(0)
      }
    },
    filterNotificationsByAgentId(id) {
      return this.getEmailsNotification.filter(
        notification => notification.agentId === id
      )
    },
    filterNotificationsGroupByAgentId(id) {
      return this.getGroupEmailsNotification.filter(
        notification => notification.agentId === id
      )
    },
    hasNoTolerance(conditions) {
      return
      !(
        conditions.hasOwnProperty('mrn:agent_condition:movement_start') ||
        conditions.hasOwnProperty('mrn:agent_condition:fuel_cut_off') ||
        conditions.hasOwnProperty('mrn:agent_condition:tamper_sensor') ||
        conditions.hasOwnProperty('mrn:agent_condition:semi_detach') ||
        conditions.hasOwnProperty('mrn:agent_condition:semi_detach_normal') ||
        conditions.hasOwnProperty('mrn:agent_condition:cargo_gate') ||
        conditions.hasOwnProperty('mrn:agent_condition:cargo_gate_normal') ||
        conditions.hasOwnProperty('mrn:agent_condition:driver_door') ||
        conditions.hasOwnProperty('mrn:agent_condition:driver_door_normal') ||
        conditions.hasOwnProperty('mrn:agent_condition:panic_button') ||
        conditions.hasOwnProperty('mrn:agent_condition:lasso_disconnected')
      )
    },
    tolerancia(key) {
      const label = GlobalHelpers.getWindowsLabel(Object.keys(key))
      return label
    },
    deleteAgent(item) {
      const { label: agentLabel } = item

      this.showDialog = true
      this.dialog.text = i18n.t(
        'fleet_management.agents.delete_agent.labels.dialog_text',
        { agentLabel }
      )
      this.dialog.type = 'warning'
      this.dialog.closeButtonText = i18n.t(
        'fleet_management.agents.delete_agent.labels.dialog_closeButton'
      )
      this.dialog.acceptButtonText = i18n.t(
        'fleet_management.agents.delete_agent.labels.dialog_acceptButton'
      )

      this.agentToDelete = item
    },
    cancelDialog() {
      this.showDialog = false
      Object.assign(this.dialog, this.dialogDefault)
    },
    async submitAgentDelete() {
      const id = this.agentToDelete.id
      try {
        this.dialog.loading = true
        await agentsHelpers.deleteAgent(id)
        this.dialog.loading = false
        this.dialog.type = 'success'
        this.dialog.text = i18n.t('general.dialogComponent.title_success')
        this.dialog.oneButtonDialog = true
        this.dialog.oneButtonText = i18n.t(
          'general.dialogComponent.acceptButtonText'
        )
        this.getAgents()
      } catch (error) {
        const errorText = agentsHelpers.getErrorMessageByCode(error.error)
        this.dialog.loading = false
        this.dialog.type = 'error'
        this.dialog.text = errorText
        this.dialog.oneButtonDialog = true
        this.dialog.oneButtonText = i18n.t(
          'general.dialogComponent.acceptButtonText'
        )
      } finally {
        if (this.getSearchInput.length && this.getSearchInput.length > 0) {
          this.resetSearchInput = true
          this.$emit('resetSearchInput', this.resetSearchInput)
        }
        this.page = 1
      }
    },
    async getAgents() {
      this.loading = true
      try {
        await this.fillAgents()
        if (this.isAdmin || this.isAdminSubFleet) {
          await this.fetchNotifications()
        }
        this.loading = false
      } catch (e) {
        this.loading = false
        this.dataMessage = `${i18n.t(
          'fleet_management.agents.data_table.labels.data_table_error'
        )}`
      }
    },
    async highlightSearchMatchAndFetchNotifications() {
      this.eventsCalled++
      if (this.eventsCalled < 2) {
        await this.fetchNotifications()
      }
      setTimeout(() => {
        if (this.resetSearchInput) {
          this.searchAndHighlight('')
          this.resetSearchInput = false
          this.$emit('resetSearchInput', this.resetSearchInput)
        } else {
          this.searchAndHighlight(this.getSearchInput)
        }
      }, 5)
    },
    async fetchNotifications() {
      this.$store.dispatch('persons/resetPersonsState')
      this.$store.dispatch('notifications/resetNotificationState')
      try {
        await this.$store.dispatch('persons/getPersonsList')
        return await this.fillEmailNotifications()
      } catch (e) {
        console.error(e)
        return e
      }
    },
    fillEmailNotifications() {
      const pageAgents = this.sortedAgents.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      )

      const promisesFillNotification = pageAgents.map(agent =>
        this.fillNotification({ agentId: agent.id })
      )

      this.eventsCalled = 0
      return Promise.all(promisesFillNotification)
    },
    resize() {
      const container = document.querySelector('.container.container--fluid')
        .offsetHeight
      const titleAndSearch = document.getElementById('titleAndSearch')
        .offsetHeight
      const tableHeader = document.querySelector('.card--title').offsetHeight
      const footer = document.querySelector('.v-footer').offsetHeight
      //viewPortHeight and we subtract the height of all the other elements
      const height = container - titleAndSearch - tableHeader - footer
      // 36 is the size of the padding that is not calculated before
      this.dataTableHeight = height - 72
    },
    contentBadge(item) {
      let value = 0
      if (item.hasOwnProperty('things')) {
        value = Object.values(item.things).length
      }

      return value
    },
    showAgentDetail(item) {
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const mainThis = this
      mainThis.$router.push(`agents/${item.id}`)
    },
    searchAndHighlight(inputTerm) {
      const keyword = inputTerm
      const marked = this.markInstance?.unmark ? this.markInstance : new Mark()
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const actualThis = this
      this.searchMatch = []

      marked.unmark({
        done: function() {
          marked.mark(keyword, {
            separateWordSearch: false,
            noMatch: function(term) {
              // Esta funcion se ejecuta cuando no hay match
            },
            each: function(node) {
              // Array con los row que matchean a la busqueda
              actualThis.searchMatch.push(node.closest('tr'))
            }
          })
        }
      })

      this.setSearchMatch(this.searchMatch)
      //Damos estilo al match activo si el listado de nodos encontrados es mayor a 0
      const activePosition = document.querySelectorAll('mark')
      if (activePosition.length > 0)
        activePosition[this.matchPosition - 1].classList.add('activeMatch')

      // Scrollintoview para el match activo
      // if (this.searchMatch.length > 0) {
      //   const selectedRow = this.searchMatch[this.matchPosition - 1]
      //   const dataTablelastRow = document.querySelector(
      //     '#agents__dataTable .v-data-table__wrapper table tbody tr:last-child'
      //   )
      //   const tableHeader = document.querySelector('.v-data-table-header')
      //     .clientHeight
      //   const barToScroll = document.querySelector(
      //     '#agents__dataTable .v-data-table__wrapper'
      //   )
      //   selectedRow.scrollIntoView()
      //   if (selectedRow === dataTablelastRow) {
      //     barToScroll.scrollBy({
      //       top: 0,
      //       behaviour: 'smooth'
      //     })
      //   } else {
      //     barToScroll.scrollBy({
      //       top: -tableHeader,
      //       behaviour: 'smooth'
      //     })
      //   }
      // }
    },
    isNotLowBattery(item) {
      return !item.conditions['mrn:agent_condition:low_battery']
    }
  },
  computed: {
    ...mapGetters('auth', ['session']),
    ...mapGetters(['getSearchInput', 'getMatchPosition']),
    ...mapGetters('events', ['agents']),
    ...mapGetters('notifications', [
      'getEmailsNotification',
      'getGroupEmailsNotification'
    ]),
    ...mapGetters('things', ['getThings']),
    ...mapGetters(['isAdminSubFleet']),
    temperatureKeys() {
      const multiTemp = this.agents?.conditions?.["mrn:agent_condition:multi_temperature"]
      if (!multiTemp) return []

      return Object.keys(multiTemp).filter(key =>
        key.startsWith("temperature")
      )
    },
    filteredAgents() {
      let agents = []
      if (this.isAdminSubFleet) {
        agents = this.agents.filter(agent => {
          if (typeof agent.things === 'object' && agent.things !== null) {
            return Object.keys(agent.things).every(key => this.getThings.some(vehicle => vehicle.name === agent.things[key]))
          }
          return false
        })
      }
      agents = this.agents.filter(agent => !agent.user_id || agent.user_id === localStorage.getItem('user_id'))
      return agents
    },
    isAdmin() {
      return store.getters.isAdmin
    },
    sortedAgents() {
      return this.sortedBy === 'conditions' && !this.sortedDesc
        ? this.agents.sort((a, b) =>
            Object.keys(a.conditions)[0] > Object.keys(b.conditions)[0] ? 1 : -1
          )
        : this.sortedBy === 'conditions' && this.sortedDesc
        ? this.agents.sort((a, b) =>
            Object.keys(a.conditions)[0] < Object.keys(b.conditions)[0] ? 1 : -1
          )
        : this.sortedDesc
        ? this.agents.sort((a, b) =>
            a.label.toLowerCase() < b.label.toLowerCase() ? 1 : -1
          )
        : this.agents.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          )
    },
    totalAgents() {
      return this.filteredAgents.length
    },
    totalPages() {
      return Math.ceil(this.totalAgents / this.itemsPerPage)
    },
    height() {
      return this.dataTableHeight
    },
    headersFiltered() {
      let headers = this.headers
      const notificationsHeader = [
        {
          text: i18n.t(
            'fleet_management.agents.data_table.labels.data_table_header_user_notificactions'
          ),
          width: 170,
          value: 'user_notifications',
          align: 'center',
          class: 'secondary--text',
          sortable: false
        }
      ]
      if (this.isAdmin || this.isAdminSubFleet) {
        headers = headers
          .slice(0, 3)
          .concat(notificationsHeader)
          .concat(headers.slice(3))
      }
      return headers
    }
  },
  watch: {
    async getSearchInput(value) {
      if (value) {
        this.loading = true
        this.$emit('setLoading', true)
        await this.highlightSearchMatchAndFetchNotifications()
        this.loading = false
        this.$emit('setLoading', false)
      }
    },
    getMatchPosition() {
      this.matchPosition = this.getMatchPosition
      const keyword = this.getSearchInput
      this.searchAndHighlight(keyword)
    },
    session(newSession) {
      //En caso que al inicio no hubiera una sesión abierta, observamos su estado
      //y cuando la misma este disponible obtenemos los datos
      //Escenario de uso: frente a un reload de página
      if (newSession.isOpen) this.getAgents()
    }
  },
  async created() {
    this.loading = true
    //Si la sesión de la conexión wamp se encuentra abierta obtengo los datos
    if (this.session.isOpen) await this.getAgents()
    this.setSearch('')
  },
  mounted() {
    document
      .getElementsByClassName('v-text-field__slot')[0]
      .setAttribute('id', 'idEventosBuscar')
    addEventListener('resize', this.resize)
    this.$nextTick(function() {
      this.resize()
    })
  },
  updated() {
    this.$nextTick(function() {
      this.context = document.querySelectorAll('.searchable')
      this.markInstance = new Mark(this.context)
    })
  },
  beforeDestroy() {
    this.$store.dispatch('persons/resetPersonsState')
    removeEventListener('resize', this.resize)
  }
}
</script>

<style lang="scss" scoped>
.scrollable-card {
  max-height: 200px;
  overflow-y: auto;
}
.v-menu__action-column {
  &.v-menu__content {
    contain: initial;
    overflow: visible;
  }
  &.v-menu__content::before {
    position: absolute;
    content: '';
    top: 50%;
    right: -12px;
    transform: translateY(-50%);
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
  }
}
.v-btn.btn__activator-not-focus:not(.v-btn--text):not(.v-btn--outlined):focus:before {
  opacity: 0;
}
.agent__label:hover {
  text-decoration: underline;
  color: var(--deep-sky-blue) !important;
}

span.label-links:hover {
  text-decoration-line: underline;
  color: var(--deep-sky-blue) !important;
}
/* .event__condition--first:first-child {
  color: var(--deep-sky-blue) !important;
} */
</style>
